import React from 'react';
import Label from '../Base/Label/Label';
import { useController, useFormContext } from 'react-hook-form';
import { joinClasses } from '../../Utils/className.util';
import Text from '../Base/Text/Text';
import { useFormContainerContext } from '../../Services/FormContainerContext';
// @ts-ignore
import styles from './FormControl.module.scss';
import Tooltip from '../Popup/Tooltip';
import useTranslation from '../../Hooks/useTranslation';

interface FormControlProps {
	label?: string | React.ReactChild;
	name?: string;
	children?: React.ReactChild;
	onChange?: (value) => void;
	wrapperClassName?: string;
	labelStyle?: object;
	isRequired?: boolean;
	hideRequired?: boolean;
	inLine?: boolean;
	isFormContextDisabled?: boolean;
	labelTooltip?: string;
	contentWidth?: number;
}

export interface IValidationError {
	message: string;
	value?: IValidationError;
}

const FormControl = (props: FormControlProps) => {
	const {
		label,
		children,
		name,
		onChange,
		wrapperClassName,
		labelStyle,
		isRequired = false,
		inLine = false,
		hideRequired = false,
		isFormContextDisabled = false,
		labelTooltip,
		contentWidth,
	} = props;

	const { t } = useTranslation();

	const { name: formContainerName } = isFormContextDisabled
		? { name: '' }
		: useFormContainerContext();

	const finalName = formContainerName ? `${formContainerName}.${name}` : name;

	const { control } = useFormContext();
	const {
		field,
		fieldState: { error },
	} = useController({
		control,
		name: finalName || '',
	});

	const handleChange = value => {
		if (typeof value?.nativeEvent?.value !== 'undefined') {
			value = value.nativeEvent.value;
		}

		field?.onChange(value);
		onChange?.(value);
	};

	let component;

	let content = name
		? React.Children.map(children, child => {
				if (React.isValidElement(child)) {
					return React.cloneElement(child, {
						...child.props,
						value: field?.value,
						error: error,
						onChange: handleChange,
					});
				}
				return child;
		  })
		: children;

	if (contentWidth) {
		content = <div style={{ width: `${contentWidth}px` }}>{content}</div>;
	}

	if (!inLine) {
		component = (
			<div
				className={joinClasses([
					wrapperClassName ?? styles.wrapper,
					error && styles.invalid,
				])}>
				<div
					style={{
						justifyContent: 'space-between',
						display: 'flex',
						alignItems: 'end',
					}}>
					{label ? (
						<Label
							tx={t(label)}
							style={labelStyle}
							tooltip={labelTooltip}
							firstUpper
						/>
					) : (
						<span />
					)}
					{!isRequired && !hideRequired && (
						<Text className={styles.optional} tx={'base.notRequired'} />
					)}
				</div>
				{content}
			</div>
		);
	} else {
		component = (
			<div
				className={joinClasses([
					wrapperClassName ?? styles.wrapperInline,
					error && styles.invalid,
				])}>
				{!isRequired && !hideRequired && (
					<Text className={styles.optional} tx={'base.notRequired'} />
				)}
				<div className={styles.inlineInputWrapper}>
					<div>
						{label && (
							<Label
								tx={label}
								style={labelStyle}
								firstUpper
								isMaxContentWidth
								tooltip={labelTooltip}
							/>
						)}
					</div>
					<div style={{ flexGrow: '1' }}>{content}</div>
				</div>
			</div>
		);
	}

	return component;
};

export default FormControl;
